"use client";

import clsx from "clsx";
import Image from "next/image";
import { useId, useState } from "react";
import { MarketingVisualBackdrop } from "@/components/redesign/MarketingVisualBackdrop";

export type MarketingCapabilityTab = {
  id: string;
  label: string;
  imageSrc: string;
  imageAlt: string;
};

export type MarketingCapabilitiesSectionProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  tabs: MarketingCapabilityTab[];
  className?: string;
};

export function MarketingCapabilitiesSection({
  eyebrow,
  title,
  subtitle,
  tabs,
  className,
}: Readonly<MarketingCapabilitiesSectionProps>) {
  const baseId = useId();
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? "");
  const activeTab =
    tabs.find((tab) => tab.id === activeTabId) ?? tabs[0] ?? null;

  if (!activeTab) {
    return null;
  }

  return (
    <section className={clsx("w-full px-4 pb-16 md:pb-24", className)}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="max-w-xl">
          <p className="font-monument text-sm text-black/70">
            {eyebrow}
          </p>
          <h2 className="mt-3 font-monument text-[2rem] leading-[1.12] tracking-tight text-black md:text-[2.5rem]">
            {title}
          </h2>
          <p className="mt-3 font-monument text-base leading-relaxed text-black/70 md:text-lg">
            {subtitle}
          </p>
        </div>

        <div
          role="tablist"
          aria-label={title}
          className="mt-10 flex gap-8 overflow-x-auto border-b border-black/10 [-ms-overflow-style:none] [scrollbar-width:none] md:mt-12 md:gap-12 md:overflow-x-visible [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab.id;
            const tabId = `${baseId}-tab-${tab.id}`;
            const panelId = `${baseId}-panel-${tab.id}`;

            return (
              <button
                key={tab.id}
                id={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                onClick={() => setActiveTabId(tab.id)}
                className={clsx(
                  "-mb-px shrink-0 border-b-2 pb-3 font-monument text-base transition-colors md:text-lg",
                  isActive
                    ? "border-black font-medium text-black"
                    : "border-transparent text-black/70 hover:text-black/60",
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          id={`${baseId}-panel-${activeTab.id}`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${activeTab.id}`}
          className="relative mt-8 overflow-hidden rounded-2xl md:mt-10 md:rounded-3xl"
        >
          <MarketingVisualBackdrop />
          <div className="relative aspect-4/3 md:aspect-16/10">
            {tabs.map((tab, index) => {
              const isActive = tab.id === activeTab.id;

              return (
                <div
                  key={tab.id}
                  aria-hidden={!isActive}
                  className={clsx(
                    "absolute inset-0 flex items-center justify-center p-4 transition-opacity duration-200 sm:p-6 md:p-10",
                    isActive
                      ? "pointer-events-auto z-10 opacity-100"
                      : "pointer-events-none z-0 opacity-0",
                  )}
                >
                  <Image
                    src={tab.imageSrc}
                    alt={isActive ? tab.imageAlt : ""}
                    width={2400}
                    height={1500}
                    sizes="(max-width: 768px) 100vw, 1152px"
                    priority={index === 0}
                    loading={index === 0 ? undefined : "eager"}
                    className="h-auto max-h-full w-full max-w-full object-contain drop-shadow-[0_24px_48px_-24px_rgba(15,40,80,0.45)]"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
